import React, { useState } from 'react';

const SIZE_PX = { xs: 24, sm: 32, md: 40, lg: 48, xl: 64 };

// Deterministic hue from a name, so the same guest always gets the same color.
const HUES = [220, 280, 330, 0, 25, 45, 150, 175, 200];
const hueFor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length];
};

const initialsFor = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Avatar — user / guest identity. Shows an image, falling back to colored
 * initials derived deterministically from the name.
 *
 * Props:
 *   name: string                 — for initials + tint + alt text
 *   src: string                  — optional image
 *   size: 'xs'|'sm'|'md'|'lg'|'xl' | number
 *   shape: 'circle' | 'rounded'  — default 'circle'
 *   status: 'online'|'busy'|'away'|'offline'  — optional presence dot
 */
const Avatar = ({ name = '', src, size = 'md', shape = 'circle', status, className = '', style, ...rest }) => {
  const [failed, setFailed] = useState(false);
  const px = typeof size === 'number' ? size : SIZE_PX[size] || 40;
  const hue = hueFor(name);
  const showImg = src && !failed;

  return (
    <span
      className={`ps-avatar ps-avatar--${shape} ${className}`.trim()}
      style={{
        width: px,
        height: px,
        fontSize: Math.round(px * 0.4),
        '--av-bg': `hsl(${hue} 70% 92%)`,
        '--av-fg': `hsl(${hue} 55% 35%)`,
        ...style,
      }}
      title={name || undefined}
      {...rest}
    >
      {showImg ? (
        <img className="ps-avatar__img" src={src} alt={name} onError={() => setFailed(true)} />
      ) : (
        <span className="ps-avatar__initials" aria-hidden="true">{initialsFor(name)}</span>
      )}
      {status && <span className={`ps-avatar__status ps-avatar__status--${status}`} />}
    </span>
  );
};

/**
 * AvatarGroup — overlapping avatars with a "+N" overflow chip.
 *
 * Props:
 *   max: number                  — how many to show before collapsing
 *   size, shape: passed to each Avatar
 *   items: Array<{ name, src }>  — or pass <Avatar> children
 */
export const AvatarGroup = ({ items, max = 4, size = 'md', shape = 'circle', className = '', children }) => {
  const list = items || React.Children.toArray(children).map((c) => c.props);
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <span className={`ps-avatar-group ${className}`.trim()}>
      {shown.map((p, i) => <Avatar key={i} size={size} shape={shape} {...p} />)}
      {extra > 0 && (
        <span
          className={`ps-avatar ps-avatar--${shape} ps-avatar--more`}
          style={{ width: SIZE_PX[size] || size, height: SIZE_PX[size] || size, fontSize: Math.round((SIZE_PX[size] || size) * 0.36) }}
        >
          +{extra}
        </span>
      )}
    </span>
  );
};

export default Avatar;
